import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Add, AddCircleOutline, ExpandLess, ExpandMore } from "@mui/icons-material";
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { toast } from "sonner";

import {
  InventorySchema,
  PlanInventorySelection,
  PlanResourceAssociation,
  PlanSchema,
  PlanType,
  PlanRecurrenceOptions,
  ResourceInventorySchema,
  ResourcePlanSchema,
  ResourceSchema,
  ResourceType,
} from "../../data/types";
import { useLoadCommunityResourcesOnly, useLoadCurrentResource, useLoadResourceInventoryItems } from "../../hooks/resourcehooks";
import { addNewResource } from "../../api/crud";
import { AddProductModal, AddResourceModal } from "../AppModal";
import { ReactComponent as DeleteIconSvg } from "../../icons/DeleteIcon.svg";
import { capitalizeString } from "../../utils/helpers";

const RESOURCE_TYPES: ResourceType[] = [
  "kitchen",
  "documentation",
  "training",
  "funding",
  "expert",
  "farm",
];

const EMPTY_INVENTORY: PlanInventorySelection[] = [];

/**
 * Preset caps for how many resources a dual membership tier may link.
 * Switch to `UNLIMITED` or a higher number when multi-resource dual tiers are supported.
 */
export const AssociatedResourceLimit = {
  /** One linked resource (current dual-tier default). */
  SINGLE: 1,
  /** No cap — allow any number of associated resources. */
  UNLIMITED: null,
} as const;

/** Active or future cap: a finite positive count, or `null` for no limit. */
export type AssociatedResourceLimitValue =
  | typeof AssociatedResourceLimit.SINGLE
  | typeof AssociatedResourceLimit.UNLIMITED
  | number;

/**
 * Active cap for `associated_resources` on dual membership tiers.
 * Adjust this when dual member tiers should allow more than one linked resource.
 */
export const MAX_ASSOCIATED_RESOURCES_PER_TIER: AssociatedResourceLimitValue =
  AssociatedResourceLimit.SINGLE;

/** Whether another associated resource can be linked given the current count and active limit. */
export const canAddAssociatedResource = (
  currentCount: number,
  limit: AssociatedResourceLimitValue = MAX_ASSOCIATED_RESOURCES_PER_TIER
): boolean => {
  if (limit == null) return true;
  return currentCount < limit;
};

/** Trim associations to the configured max (no-op when unlimited). */
export const limitAssociatedResources = <T,>(
  associations: T[],
  limit: AssociatedResourceLimitValue = MAX_ASSOCIATED_RESOURCES_PER_TIER
): T[] => {
  if (limit == null || associations.length <= limit) return associations;
  return associations.slice(0, limit);
};

/** Whether max_duration should be shown and entered in days (weekly/monthly/annual tiers). */
const usesDayDuration = (recurrence?: PlanRecurrenceOptions): boolean =>
  recurrence === "weekly" || recurrence === "monthly" || recurrence === "annual";

/** Resource/inventory linking is only available for weekly, monthly, and annual tiers. */
export const allowsTierResourceFeatures = (recurrence?: PlanRecurrenceOptions): boolean =>
  usesDayDuration(recurrence);
/** Label for the per-resource max duration field, aligned with tier billing period. */
const getMaxDurationLabel = (recurrence?: PlanRecurrenceOptions): string =>
  usesDayDuration(recurrence) ? "Max duration (days)" : "Max duration (hrs)";

/** Convert stored hours to the unit shown in the form. */
const toDisplayDuration = (hours: number, recurrence?: PlanRecurrenceOptions): number =>
  usesDayDuration(recurrence) ? Math.floor((hours || 0) / 24) : hours || 0;

/** Convert user input back to stored hours. */
const toStoredDuration = (value: number, recurrence?: PlanRecurrenceOptions): number => {
  const safe = Math.max(0, value || 0);
  return usesDayDuration(recurrence) ? safe * 24 : safe;
};

const LoadingSpinner = ({ size = 18 }: { size?: number }) => (
  <div
    className="border-2 border-[#E9E9E9] border-t-primary-300 rounded-full animate-spin shrink-0"
    style={{ height: size, width: size }}
    role="status"
    aria-label="Loading"
  />
);

const LoadingFeedback = ({ label }: { label: string }) => (
  <div className="flex items-center gap-2 text-[13px] font-open-sans text-[#808080]">
    <LoadingSpinner size={16} />
    <span>{label}</span>
  </div>
);

const SectionLoadingOverlay = ({ show, label }: { show: boolean; label: string }) => {
  if (!show) return null;

  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/75 backdrop-blur-[1px]"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center gap-2 px-4 py-3 rounded-lg bg-white border border-[#E9E9E9] shadow-sm">
        <LoadingSpinner size={22} />
        <span className="text-[13px] font-open-sans text-[#4D4D4D] text-center">{label}</span>
      </div>
    </div>
  );
};

export const getResourceInventoryItems = (
  resource?: ResourceSchema | null,
  fetchedInventory?: unknown
): InventorySchema[] => {
  const inv = resource?.resource_inventory as ResourceInventorySchema | undefined;
  const embeddedItems = inv?.items;
  if (Array.isArray(embeddedItems) && embeddedItems.length > 0) {
    return embeddedItems;
  }

  const scheduleItems = inv?.schedule;
  if (Array.isArray(scheduleItems) && scheduleItems.length > 0) {
    return scheduleItems as InventorySchema[];
  }

  const normalizedFetch =
    fetchedInventory && typeof fetchedInventory === "object" && "body" in (fetchedInventory as object)
      ? (fetchedInventory as { body?: unknown }).body
      : fetchedInventory;

  if (Array.isArray(normalizedFetch)) {
    if (
      normalizedFetch.length === 0 ||
      (normalizedFetch[0] as InventorySchema)?.item_name !== undefined
    ) {
      return normalizedFetch as InventorySchema[];
    }
  }

  if (normalizedFetch && typeof normalizedFetch === "object") {
    const nestedItems = (normalizedFetch as ResourceInventorySchema).items;
    if (Array.isArray(nestedItems)) {
      return nestedItems;
    }
    const nestedSchedule = (normalizedFetch as ResourceInventorySchema).schedule;
    if (Array.isArray(nestedSchedule)) {
      return nestedSchedule as InventorySchema[];
    }
  }

  return [];
};

/** Upper bound for max_qty based on the inventory item's total_quantity. */
export const getInventoryItemMaxQty = (item: InventorySchema): number => {
  const total = item.total_quantity;
  if (typeof total === "number" && total > 0) {
    return total;
  }
  return 1;
};

export const clampInventoryMaxQty = (qty: number, item: InventorySchema): number => {
  const maxAllowed = getInventoryItemMaxQty(item);
  return Math.min(Math.max(1, qty), maxAllowed);
};

export const normalizePlanResourceAssociation = (
  association: PlanResourceAssociation & { inventory_ids?: string[] }
): PlanResourceAssociation => {
  if (association.inventory?.length) {
    return association;
  }
  if (association.inventory_ids?.length) {
    return {
      ...association,
      inventory: association.inventory_ids.map((inventory_id) => ({
        inventory_id,
        max_qty: 1,
      })),
    };
  }
  return { ...association, inventory: association.inventory || [] };
};

type PlanTierResourceFeaturesProps = {
  communityId: string;
  planRecurrence?: PlanRecurrenceOptions;
  associatedResources: PlanResourceAssociation[];
  onChange: (resources: PlanResourceAssociation[]) => void;
  /** When set, each linked resource uses this stored-hour value for max_duration (dual tiers). */
  planMaxHours?: number;
};

type ResourceInventoryPanelProps = {
  resource: ResourceSchema;
  selectedInventory: PlanInventorySelection[];
  onInventoryChange: (inventory: PlanInventorySelection[]) => void;
  onRequestAddInventory: () => void;
  refreshToken?: number;
};

type InventoryItemRowProps = {
  item: InventorySchema;
  selected: boolean;
  maxQty: number;
  onToggle: (item: InventorySchema) => void;
  onMaxQtyChange: (itemId: string, maxQty: number, item: InventorySchema) => void;
};

const InventoryItemRow = memo<InventoryItemRowProps>(({
  item,
  selected,
  maxQty,
  onToggle,
  onMaxQtyChange,
}) => {
  const itemId = item.id || "";
  const maxAllowed = getInventoryItemMaxQty(item);

  return (
    <div
      className={`rounded-lg px-2 py-2 transition-colors ${
        selected ? "bg-white border border-primary-200/30" : "hover:bg-white/60"
      }`}
    >
      <button
        type="button"
        className="flex flex-row items-center gap-x-2 text-left w-full"
        onClick={() => onToggle(item)}
      >
        {selected ? (
          <CheckBox className="text-primary-300 shrink-0" style={{ height: 18, width: 18 }} />
        ) : (
          <CheckBoxOutlineBlank
            className="text-[#CFCFCF] shrink-0"
            style={{ height: 18, width: 18 }}
          />
        )}
        <span className="text-[14px] font-open-sans text-black flex-1">
          {item.item_name}
        </span>
        <span className="text-[12px] font-open-sans text-[#808080] shrink-0">
          {maxAllowed} available
        </span>
      </button>

      {selected && (
        <div className="flex flex-row items-center gap-x-2 ml-7 mt-2">
          <label className="text-[13px] font-open-sans text-[#808080] shrink-0">
            Qty included
          </label>
          <input
            type="number"
            min={1}
            max={maxAllowed}
            className="border-[1px] border-[#E9E9E9] bg-white w-[72px] rounded-lg px-2 py-1 text-sm text-black font-open-sans"
            value={maxQty}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onMaxQtyChange(itemId, Number(e.target.value) || 1, item)}
          />
          <span className="text-[12px] font-open-sans text-[#808080]">
            of {maxAllowed}
          </span>
        </div>
      )}
    </div>
  );
});
InventoryItemRow.displayName = "InventoryItemRow";

type ResourceInventoryPanelContentProps = ResourceInventoryPanelProps & {
  embeddedItems: InventorySchema[];
  hasEmbeddedItems: boolean;
};

const ResourceInventoryPanelContent = memo<ResourceInventoryPanelContentProps>(({
  resource,
  selectedInventory,
  onInventoryChange,
  onRequestAddInventory,
  refreshToken = 0,
  embeddedItems,
  hasEmbeddedItems,
}) => {
  const resourceId = resource.id || "";
  const needsNetworkFetch = !hasEmbeddedItems || refreshToken > 0;

  const { currentResource, reloadCurrentResource, isLoadingResource } =
    useLoadCurrentResource(resourceId, needsNetworkFetch, false);
  const { inventoryList, reloadInventoryList, isLoadingInventoryList } =
    useLoadResourceInventoryItems(resourceId, needsNetworkFetch, false);

  const items = useMemo(() => {
    if (hasEmbeddedItems && !currentResource && !inventoryList) {
      return embeddedItems;
    }
    return getResourceInventoryItems(currentResource || resource, inventoryList);
  }, [hasEmbeddedItems, embeddedItems, currentResource, resource, inventoryList]);

  const itemsById = useMemo(() => {
    const map = new Map<string, InventorySchema>();
    for (const item of items) {
      if (item.id) map.set(item.id, item);
    }
    return map;
  }, [items]);

  const selectedLookup = useMemo(() => {
    const ids = new Set<string>();
    const qtyById = new Map<string, number>();
    for (const selection of selectedInventory) {
      ids.add(selection.inventory_id);
      qtyById.set(selection.inventory_id, selection.max_qty);
    }
    return { ids, qtyById };
  }, [selectedInventory]);

  useEffect(() => {
    if (!resourceId || refreshToken === 0) return;
    reloadInventoryList();
    reloadCurrentResource();
  }, [resourceId, refreshToken, reloadInventoryList, reloadCurrentResource]);

  const isInventoryLoading = isLoadingInventoryList || isLoadingResource;

  const allSelected = useMemo(
    () =>
      items.length > 0 &&
      items.every((item) => item.id && selectedLookup.ids.has(item.id)),
    [items, selectedLookup.ids]
  );

  const toggleInventoryItem = useCallback(
    (item: InventorySchema) => {
      const itemId = item.id || "";
      if (!itemId) return;

      if (selectedLookup.ids.has(itemId)) {
        onInventoryChange(
          selectedInventory.filter((selection) => selection.inventory_id !== itemId)
        );
        return;
      }

      onInventoryChange([
        ...selectedInventory,
        { inventory_id: itemId, max_qty: getInventoryItemMaxQty(item) },
      ]);
    },
    [onInventoryChange, selectedInventory, selectedLookup.ids]
  );

  const updateMaxQty = useCallback(
    (itemId: string, maxQty: number, item: InventorySchema) => {
      onInventoryChange(
        selectedInventory.map((selection) =>
          selection.inventory_id === itemId
            ? { ...selection, max_qty: clampInventoryMaxQty(maxQty, item) }
            : selection
        )
      );
    },
    [onInventoryChange, selectedInventory]
  );

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      onInventoryChange([]);
      return;
    }

    onInventoryChange(
      items
        .filter((item) => item.id)
        .map((item) => ({
          inventory_id: item.id as string,
          max_qty: getInventoryItemMaxQty(item),
        }))
    );
  }, [allSelected, items, onInventoryChange]);

  return (
    <div className="mt-2 pl-1">
      {isInventoryLoading ? (
        <LoadingFeedback label="Loading inventory..." />
      ) : items.length > 0 ? (
        <div className="grid grid-flow-row gap-y-1">
          <button
            type="button"
            className="flex flex-row items-center gap-x-2 text-left w-fit px-2 py-1 rounded-md hover:bg-white/80"
            onClick={toggleSelectAll}
          >
            {allSelected ? (
              <CheckBox className="text-primary-300" style={{ height: 18, width: 18 }} />
            ) : (
              <CheckBoxOutlineBlank
                className="text-[#CFCFCF]"
                style={{ height: 18, width: 18 }}
              />
            )}
            <span className="text-[14px] font-open-sans text-[#4D4D4D]">Select all items</span>
          </button>

          <div className="max-h-[220px] overflow-y-auto grid grid-flow-row gap-y-1 pr-1">
            {items.map((item) => {
              const itemId = item.id || "";
              const selected = selectedLookup.ids.has(itemId);
              const rawQty = selectedLookup.qtyById.get(itemId) ?? 1;
              const itemRef = itemsById.get(itemId);
              const maxQty = itemRef ? clampInventoryMaxQty(rawQty, itemRef) : rawQty;

              return (
                <InventoryItemRow
                  key={itemId || item.item_name}
                  item={item}
                  selected={selected}
                  maxQty={maxQty}
                  onToggle={toggleInventoryItem}
                  onMaxQtyChange={updateMaxQty}
                />
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-[13px] text-[#808080] font-open-sans py-2">
          No inventory items for this resource yet. Add one below to include it with this tier.
        </p>
      )}

      <button
        type="button"
        className="w-fit border-0 text-primary-300 font-raleway font-medium rounded-md px-2 py-1.5 text-sm flex flex-row items-center mt-2 hover:text-primary-200"
        onClick={onRequestAddInventory}
      >
        <Add className="text-primary-300 mr-1" style={{ height: 16, width: 16 }} />
        Add inventory item
      </button>
    </div>
  );
});
ResourceInventoryPanelContent.displayName = "ResourceInventoryPanelContent";

const ResourceInventoryPanel = memo<ResourceInventoryPanelProps>(({
  resource,
  selectedInventory,
  onInventoryChange,
  onRequestAddInventory,
  refreshToken = 0,
}) => {
  const [expanded, setExpanded] = useState(() => selectedInventory.length > 0);
  const embeddedItems = useMemo(
    () => getResourceInventoryItems(resource),
    [resource]
  );
  const hasEmbeddedItems = embeddedItems.length > 0;
  const selectedCount = selectedInventory.length;
  const displayItemCount = embeddedItems.length;

  const prevResourceIdRef = useRef(resource.id);
  useEffect(() => {
    if (prevResourceIdRef.current !== resource.id) {
      prevResourceIdRef.current = resource.id;
      setExpanded(selectedInventory.length > 0);
    }
  }, [resource.id, selectedInventory.length]);

  const summaryParts: string[] = [];
  if (displayItemCount > 0) {
    summaryParts.push(`${displayItemCount} available`);
  }
  if (selectedCount > 0) {
    summaryParts.push(`${selectedCount} included`);
  }
  const summaryText = summaryParts.length > 0 ? summaryParts.join(" · ") : "Optional - choose items to bundle";

  return (
    <div className="mt-3 pt-3 border-t border-[#E9E9E9]">
      <button
        type="button"
        className="flex flex-row items-center justify-between w-full text-left group rounded-lg px-2 py-2 hover:bg-white/80 transition-colors"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <span className="flex flex-row items-center gap-x-2 min-w-0">
          {expanded ? (
            <ExpandLess className="text-[#808080] group-hover:text-primary-300 shrink-0" style={{ height: 20, width: 20 }} />
          ) : (
            <ExpandMore className="text-[#808080] group-hover:text-primary-300 shrink-0" style={{ height: 20, width: 20 }} />
          )}
          <span className="text-[14px] font-raleway font-medium text-[#4D4D4D]">
            Inventory items
          </span>
          {selectedCount > 0 && (
            <span className="text-[11px] font-open-sans bg-primary-200/10 text-primary-300 px-2 py-0.5 rounded-full shrink-0">
              {selectedCount} selected
            </span>
          )}
        </span>
        <span className="text-[12px] font-open-sans text-[#808080] ml-3 shrink-0">
          {summaryText}
        </span>
      </button>

      {expanded && (
        <ResourceInventoryPanelContent
          resource={resource}
          selectedInventory={selectedInventory}
          onInventoryChange={onInventoryChange}
          onRequestAddInventory={onRequestAddInventory}
          refreshToken={refreshToken}
          embeddedItems={embeddedItems}
          hasEmbeddedItems={hasEmbeddedItems}
        />
      )}
    </div>
  );
});
ResourceInventoryPanel.displayName = "ResourceInventoryPanel";

type ResourceAssociationRowProps = {
  association: PlanResourceAssociation;
  index: number;
  planRecurrence?: PlanRecurrenceOptions;
  getResourceById: (resourceId: string) => ResourceSchema | undefined;
  resourceOptionNodes: React.ReactNode;
  inventoryRefreshToken: number;
  onResourceSelect: (index: number, resourceId: string) => void;
  onUpdateAssociation: (index: number, updates: Partial<PlanResourceAssociation>) => void;
  onRemoveAssociation: (index: number) => void;
  onRequestAddInventory: (resourceId: string) => void;
  /** When set, max duration is driven by the parent plan's plan_max_hours. */
  lockedMaxDuration?: number;
};

const ResourceAssociationRow = memo<ResourceAssociationRowProps>(({
  association,
  index,
  planRecurrence,
  getResourceById,
  resourceOptionNodes,
  inventoryRefreshToken,
  onResourceSelect,
  onUpdateAssociation,
  onRemoveAssociation,
  onRequestAddInventory,
  lockedMaxDuration,
}) => {
  const normalizedAssociation = useMemo(
    () => normalizePlanResourceAssociation(association),
    [association]
  );

  const inventory = normalizedAssociation.inventory || EMPTY_INVENTORY;
  const resourceId = normalizedAssociation.resource_id;
  const selectedResource = resourceId ? getResourceById(resourceId) : undefined;
  const maxDurationLabel = getMaxDurationLabel(planRecurrence);
  const storedMaxDuration = lockedMaxDuration ?? normalizedAssociation.max_duration;
  const displayDuration = toDisplayDuration(storedMaxDuration, planRecurrence);

  const handleInventoryChange = useCallback(
    (next: PlanInventorySelection[]) => onUpdateAssociation(index, { inventory: next }),
    [index, onUpdateAssociation]
  );

  const handleRequestAddInventory = useCallback(
    () => onRequestAddInventory(normalizedAssociation.resource_id),
    [normalizedAssociation.resource_id, onRequestAddInventory]
  );

  return (
    <div className="bg-[#FAFAFA] border-[1px] border-[#E9E9E9] rounded-lg p-4">
      <div className="flex flex-col gap-y-3 sm:flex-row sm:items-start sm:gap-x-3">
        <div className="flex-1 min-w-0">
          <label className="text-[12px] font-raleway text-[#808080] mb-1 block">
            Resource
          </label>
          <select
            className="border-[1px] border-[#E9E9E9] bg-white w-full rounded-lg px-3 py-2 text-sm font-open-sans"
            value={normalizedAssociation.resource_id}
            onChange={(e) => onResourceSelect(index, e.target.value)}
          >
            <option value="">Select a resource</option>
            {resourceOptionNodes}
          </select>
        </div>

        <div className="w-full sm:w-[140px] shrink-0">
          <label className="text-[12px] font-raleway text-[#808080] mb-1 block">
            {maxDurationLabel}
          </label>
          {lockedMaxDuration !== undefined ? (
            <div
              className="border-[1px] border-[#E9E9E9] bg-[#FAFAFA] w-full rounded-lg px-3 py-2 text-sm font-open-sans text-[#808080]"
              title="Uses the plan maximum duration from Include Maximum Duration"
            >
              {displayDuration}
            </div>
          ) : (
            <input
              type="number"
              min={0}
              className="border-[1px] border-[#E9E9E9] bg-white w-full rounded-lg px-3 py-2 text-sm font-open-sans"
              placeholder="0"
              value={displayDuration}
              onChange={(e) =>
                onUpdateAssociation(index, {
                  max_duration: toStoredDuration(Number(e.target.value) || 0, planRecurrence),
                })
              }
            />
          )}
        </div>

        <button
          type="button"
          className="flex items-center justify-center rounded-lg h-[42px] w-[42px] border-[1px] border-[#E9E9E9] bg-white hover:bg-gray-100 shrink-0 sm:mt-[22px]"
          onClick={() => onRemoveAssociation(index)}
          title="Remove this resource link"
        >
          <DeleteIconSvg className="text-[#A1A1A1]" style={{ height: 18, width: 18 }} />
        </button>
      </div>

      {selectedResource && normalizedAssociation.resource_id && (
        <ResourceInventoryPanel
          resource={selectedResource}
          selectedInventory={inventory}
          onInventoryChange={handleInventoryChange}
          onRequestAddInventory={handleRequestAddInventory}
          refreshToken={inventoryRefreshToken}
        />
      )}
    </div>
  );
});
ResourceAssociationRow.displayName = "ResourceAssociationRow";

type CreateResourceActionsProps = {
  communityId?: string;
  onResourceCreated: (association: PlanResourceAssociation) => void;
  reloadResources: () => Promise<unknown>;
};

const CreateResourceActions = memo<CreateResourceActionsProps>(({
  communityId,
  onResourceCreated,
  reloadResources,
}) => {
  const [createResourceType, setCreateResourceType] = useState<ResourceType>("kitchen");
  const [isAddingResource, setIsAddingResource] = useState(false);

  const handleCreateResourceTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setCreateResourceType(e.target.value as ResourceType);
  }, []);

  const handleAddResource = useCallback(
    async (resource: ResourceSchema) => {
      if (!communityId || !resource?.name) {
        toast.error("Resource name is required");
        return;
      }

      const created = await addNewResource(communityId, {
        ...resource,
        resource_type: resource.resource_type || createResourceType,
        active: true,
      });

      if (created?.id) {
        toast.success("Resource created");
        await reloadResources();
        onResourceCreated({
          resource_id: created.id,
          resource_name: created.name || resource.name,
          max_duration: 0,
          inventory: [],
        });
        setIsAddingResource(false);
        return;
      }

      toast.error("Failed to create resource");
    },
    [communityId, createResourceType, onResourceCreated, reloadResources]
  );

  return (
    <>
      <div className="flex flex-row items-center gap-x-2 border-[1px] border-[#E9E9E9] rounded-lg px-2 py-1 bg-white">
        <select
          className="border-0 bg-inherit text-sm font-open-sans text-[#4D4D4D] outline-none"
          value={createResourceType}
          onChange={handleCreateResourceTypeChange}
        >
          {RESOURCE_TYPES.map((type) => (
            <option key={type} value={type}>
              {capitalizeString(type)}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="border-0 text-primary-300 font-raleway font-medium text-sm flex flex-row items-center whitespace-nowrap hover:text-primary-200"
          onClick={() => setIsAddingResource(true)}
        >
          <Add className="text-primary-300 mr-0.5" style={{ height: 16, width: 16 }} />
          Create resource
        </button>
      </div>

    {isAddingResource && (
      <AddResourceModal
        isOpen={isAddingResource}
        resourceType={createResourceType}
        handleAddResource={handleAddResource}
        closeAppModal={() => setIsAddingResource(false)}
      />
    )}
    </>
  );
});
CreateResourceActions.displayName = "CreateResourceActions";

export const PlanTierResourceFeatures: React.FC<PlanTierResourceFeaturesProps> = ({
  communityId,
  planRecurrence,
  associatedResources,
  onChange,
  planMaxHours,
}) => {
  const {
    resources,
    reloadResources,
    isLoadingResources,
  } = useLoadCommunityResourcesOnly(communityId);

  const associatedResourcesRef = useRef(associatedResources);
  associatedResourcesRef.current = associatedResources;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const availableResources = useMemo(
    () => (resources || []).filter((resource): resource is ResourceSchema & { id: string } => Boolean(resource.id)),
    [resources]
  );

  const resourcesById = useMemo(() => {
    const map = new Map<string, ResourceSchema>();
    for (const resource of availableResources) {
      if (resource.id) map.set(resource.id, resource);
    }
    return map;
  }, [availableResources]);

  const resourcesByIdRef = useRef(resourcesById);
  resourcesByIdRef.current = resourcesById;

  const getResourceById = useCallback(
    (resourceId: string) => resourcesByIdRef.current.get(resourceId),
    []
  );

  const resourceOptionNodes = useMemo(
    () =>
      availableResources.map((resource) => (
        <option key={resource.id} value={resource.id}>
          {resource.name}
          {resource.resource_type ? ` (${resource.resource_type})` : ""}
        </option>
      )),
    [availableResources]
  );

  const rowIdCounter = useRef(0);
  const [rowInstanceIds, setRowInstanceIds] = useState<number[]>(() => {
    const ids: number[] = [];
    for (let i = 0; i < associatedResources.length; i++) {
      ids.push(++rowIdCounter.current);
    }
    return ids;
  });

  useEffect(() => {
    setRowInstanceIds((prev) => {
      if (prev.length === associatedResources.length) return prev;
      if (prev.length < associatedResources.length) {
        const next = [...prev];
        while (next.length < associatedResources.length) {
          next.push(++rowIdCounter.current);
        }
        return next;
      }
      return prev.slice(0, associatedResources.length);
    });
  }, [associatedResources.length]);

  const [isAddingInventory, setIsAddingInventory] = useState(false);
  const [addInventoryForResourceId, setAddInventoryForResourceId] = useState<string | null>(null);
  const [inventoryRefreshByResourceId, setInventoryRefreshByResourceId] = useState<Record<string, number>>({});

  const addInventoryResource = useMemo(
    () => (addInventoryForResourceId ? resourcesById.get(addInventoryForResourceId) : undefined),
    [addInventoryForResourceId, resourcesById]
  );

  const updateAssociation = useCallback(
    (index: number, updates: Partial<PlanResourceAssociation>) => {
      const next = associatedResourcesRef.current.map((association, i) =>
        i === index ? { ...association, ...updates } : association
      );
      onChangeRef.current(next);
    },
    []
  );

  const removeAssociation = useCallback((index: number) => {
    onChangeRef.current(associatedResourcesRef.current.filter((_, i) => i !== index));
  }, []);

  const addAssociation = useCallback(() => {
    if (!canAddAssociatedResource(associatedResourcesRef.current.length)) return;
    onChangeRef.current([
      ...associatedResourcesRef.current,
      { resource_id: "", max_duration: planMaxHours ?? 0, inventory: [] },
    ]);
  }, [planMaxHours]);

  const handleResourceSelect = useCallback(
    (index: number, resourceId: string) => {
      const selected = resourcesByIdRef.current.get(resourceId);
      updateAssociation(index, {
        resource_id: resourceId,
        resource_name: selected?.name,
        inventory: [],
        ...(planMaxHours !== undefined ? { max_duration: planMaxHours } : {}),
      });
    },
    [planMaxHours, updateAssociation]
  );

  const handleResourceCreated = useCallback(
    (association: PlanResourceAssociation) => {
      if (!canAddAssociatedResource(associatedResourcesRef.current.length)) return;
      onChangeRef.current([...associatedResourcesRef.current, association]);
    },
    []
  );

  const handleCloseAddInventory = useCallback(async () => {
    const resourceId = addInventoryForResourceId;
    setIsAddingInventory(false);
    setAddInventoryForResourceId(null);
    if (resourceId) {
      setInventoryRefreshByResourceId((prev) => ({
        ...prev,
        [resourceId]: (prev[resourceId] || 0) + 1,
      }));
    }
    await reloadResources();
  }, [addInventoryForResourceId, reloadResources]);

  const openAddInventoryModal = useCallback((resourceId: string) => {
    setAddInventoryForResourceId(resourceId);
    setIsAddingInventory(true);
  }, []);

  /** Keep each linked resource's max_duration aligned with the parent plan's plan_max_hours (dual tiers). */
  useEffect(() => {
    if (planMaxHours === undefined) {
      return;
    }

    const needsSync = associatedResources.some(
      (association) => association.max_duration !== planMaxHours
    );
    if (!needsSync) {
      return;
    }

    onChangeRef.current(
      associatedResources.map((association) => ({
        ...association,
        max_duration: planMaxHours,
      }))
    );
  }, [associatedResources, planMaxHours]);

  const showSectionOverlay = isLoadingResources && availableResources.length === 0;
  const canAddMoreResources = canAddAssociatedResource(associatedResources.length);

  return (
    <div className="relative flex flex-col">
      <SectionLoadingOverlay show={showSectionOverlay} label="Loading resources..." />

      <span className="flex flex-row text-[#4D4D4D] text-[14px] font-raleway font-medium mb-1">
        Resources &amp; inventory
      </span>
      {/* <p className="text-[13px] text-[#808080] font-open-sans mb-2">
        Give members access to specific resources and optional inventory. Adding any turns this into a dual tier.
      </p> */}

      {associatedResources.length > 0 && (
        <div className="grid grid-flow-row gap-y-3 mb-3">
          {associatedResources.map((association, index) => (
            <ResourceAssociationRow
              key={rowInstanceIds[index] ?? index}
              association={association}
              index={index}
              planRecurrence={planRecurrence}
              getResourceById={getResourceById}
              resourceOptionNodes={resourceOptionNodes}
              inventoryRefreshToken={inventoryRefreshByResourceId[association.resource_id || ""] || 0}
              onResourceSelect={handleResourceSelect}
              onUpdateAssociation={updateAssociation}
              onRemoveAssociation={removeAssociation}
              onRequestAddInventory={openAddInventoryModal}
              lockedMaxDuration={planMaxHours}
            />
          ))}
        </div>
      )}

      {canAddMoreResources && (
        <>
          {/* Primary path: pick from resources that already exist */}
          <button
            type="button"
            className="w-full border-[1px] border-dashed border-[#CFCFCF] text-primary-300 font-raleway font-medium rounded-lg px-3 py-2.5 text-sm flex flex-row items-center justify-center hover:border-primary-300 hover:bg-primary-200/5 transition-colors"
            onClick={addAssociation}
          >
            <AddCircleOutline className="mr-1.5" style={{ height: 18, width: 18 }} />
            {associatedResources.length > 0 ? "Add another resource" : "Add an existing resource"}
          </button>

          {/* Secondary path: create a brand-new resource, clearly separated from the above */}
          <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1 mt-2">
            <span className="text-[12px] text-[#808080] font-open-sans">
              Don&apos;t see the resource you need?
            </span>
            <CreateResourceActions
              communityId={communityId}
              onResourceCreated={handleResourceCreated}
              reloadResources={reloadResources}
            />
          </div>
        </>
      )}

      {isAddingInventory && addInventoryResource?.id && addInventoryResource.resource_type && (
        <AddProductModal
          isOpen={isAddingInventory}
          resourceType={addInventoryResource.resource_type}
          resourceId={addInventoryResource.id}
          setIsOpen={setIsAddingInventory}
          setCurrentInventoryList={() => undefined}
          closeAppModal={handleCloseAddInventory}
        />
      )}
    </div>
  );
};

type PlanTierResourceFeaturesFieldProps = {
  communityId: string;
  planRecurrence?: PlanRecurrenceOptions;
  associationsRef: React.MutableRefObject<PlanResourceAssociation[]>;
  initialAssociatedResources?: PlanResourceAssociation[];
  /** Re-initializes local state only when opening a different plan (avoids clobbering user edits). */
  associationSyncKey?: string;
  /** Stored-hour plan_max_hours; when set, linked resources inherit this max_duration. */
  planMaxHours?: number;
  onAssociationsChange?: (associations: PlanResourceAssociation[]) => void;
};

const EMPTY_FIELD_ASSOCIATIONS: PlanResourceAssociation[] = [];

export const PlanTierResourceFeaturesField = memo<PlanTierResourceFeaturesFieldProps>(({
  communityId,
  planRecurrence,
  associationsRef,
  initialAssociatedResources = EMPTY_FIELD_ASSOCIATIONS,
  associationSyncKey = "new",
  planMaxHours,
  onAssociationsChange,
}) => {
  const [associatedResources, setAssociatedResources] = useState(initialAssociatedResources);

  // Keep latest initials in a ref so we only re-hydrate when associationSyncKey changes
  // (opening a different plan), not on every parent re-render that would clobber edits.
  const initialAssociatedResourcesRef = useRef(initialAssociatedResources);
  initialAssociatedResourcesRef.current = initialAssociatedResources;

  useEffect(() => {
    const next = initialAssociatedResourcesRef.current;
    setAssociatedResources(next);
    associationsRef.current = next;
  }, [associationSyncKey, associationsRef]);

  const onChange = useCallback(
    (next: PlanResourceAssociation[]) => {
      associationsRef.current = next;
      setAssociatedResources(next);
      onAssociationsChange?.(next);
    },
    [associationsRef, onAssociationsChange]
  );

  return (
    <PlanTierResourceFeatures
      communityId={communityId}
      planRecurrence={planRecurrence}
      associatedResources={associatedResources}
      onChange={onChange}
      planMaxHours={planMaxHours}
    />
  );
});
PlanTierResourceFeaturesField.displayName = "PlanTierResourceFeaturesField";

type PlanResourceInventoryFeaturesFieldProps = {
  resource: ResourceSchema;
  includeAssociatedResources: boolean;
  onIncludeAssociatedResourcesChange: (included: boolean) => void;
  associationsRef: React.MutableRefObject<PlanResourceAssociation[]>;
  initialAssociatedResources?: PlanResourceAssociation[];
  /** Stored-hour plan_max_hours applied to associated_resources.max_duration for resource plans. */
  planMaxHours?: number;
  planType: PlanType;
};

const EMPTY_RESOURCE_ASSOCIATIONS: PlanResourceAssociation[] = [];

/** Bundled-inventory editor with an integrated enable toggle for resource booking plans */
export const PlanResourceInventoryFeaturesField = memo<PlanResourceInventoryFeaturesFieldProps>(({
  resource,
  includeAssociatedResources,
  onIncludeAssociatedResourcesChange,
  associationsRef,
  initialAssociatedResources = EMPTY_RESOURCE_ASSOCIATIONS,
  planMaxHours = 0,
  planType,
}) => {
  const resourceId = resource.id || "";
  const [inventoryRefreshToken, setInventoryRefreshToken] = useState(0);
  const [isAddingInventory, setIsAddingInventory] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState<PlanInventorySelection[]>(() => {
    const association = initialAssociatedResources.find((entry) => entry.resource_id === resourceId);
    return association?.inventory || [];
  });

  const syncAssociationsRef = useCallback((inventory: PlanInventorySelection[]) => {
    if (!resourceId) {
      associationsRef.current = [];
      return;
    }

    // When associations are enabled, always keep the resource link even if no
    // inventory items are selected yet (dual plans often have empty inventory).
    if (!includeAssociatedResources) {
      return;
    }

    associationsRef.current = [{
      resource_id: resourceId,
      resource_name: resource.name,
      max_duration: planMaxHours,
      inventory,
    }];
  }, [associationsRef, includeAssociatedResources, planMaxHours, resource.name, resourceId]);

  const handleInventoryChange = useCallback((inventory: PlanInventorySelection[]) => {
    setSelectedInventory(inventory);
    syncAssociationsRef(inventory);
  }, [syncAssociationsRef]);

  // Re-initialize selection only when the resource or the incoming plan's inventory
  // actually changes. Keyed on a content signature so transient reference changes of
  // the associations ref (e.g. from this field's own writes) never wipe the selection.
  const initialInventorySignature = useMemo(() => {
    const association = initialAssociatedResources.find((entry) => entry.resource_id === resourceId);
    return (association?.inventory || [])
      .map((selection) => `${selection.inventory_id ?? ""}:${selection.max_qty ?? 0}`)
      .sort()
      .join(",");
  }, [initialAssociatedResources, resourceId]);

  useEffect(() => {
    const association = initialAssociatedResources.find((entry) => entry.resource_id === resourceId);
    const inventory = association?.inventory || [];
    setSelectedInventory(inventory);
    syncAssociationsRef(inventory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInventorySignature, resourceId]);

  useEffect(() => {
    syncAssociationsRef(selectedInventory);
  }, [planMaxHours, selectedInventory, syncAssociationsRef]);

  const handleCloseAddInventory = useCallback(() => {
    setIsAddingInventory(false);
    setInventoryRefreshToken((prev) => prev + 1);
  }, []);

  if (!resourceId) {
    return null;
  }

  const toggleLabel = planType === "dual" ? "Add Resource" : "Included Inventory";
  const toggleDescription = planType === "dual"
    ? "Include resource and inventory items on this plan."
    : "Optional items included with this booking plan.";

  return (
    <div className="flex flex-col rounded-lg bg-[#FAFAFA] px-4 py-3 gap-y-3">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-[#4D4D4D] text-[14px] font-raleway font-medium">
            {toggleLabel}
          </span>
          <p className="text-[13px] text-[#808080] font-open-sans mt-0.5">
            {toggleDescription}
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer shrink-0">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={includeAssociatedResources}
            onChange={(event) => onIncludeAssociatedResourcesChange(event.target.checked)}
            data-testid="include-associated-resources-toggle"
          />
          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 peer-focus:ring-primary-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[50%] after:translate-y-[-50%] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-400 min-w-[36px] min-h-[20px]" />
        </label>
      </div>

      {includeAssociatedResources && (
        <ResourceInventoryPanel
          resource={resource}
          selectedInventory={selectedInventory}
          onInventoryChange={handleInventoryChange}
          onRequestAddInventory={() => setIsAddingInventory(true)}
          refreshToken={inventoryRefreshToken}
        />
      )}

      {isAddingInventory && resource.resource_type && (
        <AddProductModal
          isOpen={isAddingInventory}
          resourceType={resource.resource_type}
          resourceId={resourceId}
          setIsOpen={setIsAddingInventory}
          setCurrentInventoryList={() => undefined}
          closeAppModal={handleCloseAddInventory}
        />
      )}
    </div>
  );
});
PlanResourceInventoryFeaturesField.displayName = "PlanResourceInventoryFeaturesField";

/** Builds a lookup map of inventory items by id from one or more resources */
export const buildInventoryItemsByIdFromResources = (
  resources?: ResourceSchema[]
): Map<string, InventorySchema> => {
  const map = new Map<string, InventorySchema>();

  (resources || []).forEach((entry) => {
    getResourceInventoryItems(entry).forEach((item) => {
      if (item.id) {
        map.set(item.id, item);
      }
    });
  });

  return map;
};

/** Resolves the display label for a linked inventory selection */
export const getInventorySelectionLabel = (
  selection: PlanInventorySelection,
  inventoryItemsById: Map<string, InventorySchema>
): string => {
  const item = inventoryItemsById.get(selection.inventory_id);
  return item?.item_name?.trim() || "Inventory item";
};

/** Whether perks or bundled inventory should be shown under plan features */
export const planHasDisplayFeatures = (
  features?: string[],
  associatedResources?: PlanResourceAssociation[]
): boolean => {
  const hasFeatures = (features?.length ?? 0) > 0;
  const hasInventory = (associatedResources || []).some(
    (association) => (association.inventory?.length ?? 0) > 0
  );
  return hasFeatures || hasInventory;
};

/** Whether a resource plan bundles any inventory selections */
export const resourcePlanHasInventory = (
  associatedResources?: PlanResourceAssociation[]
): boolean =>
  (associatedResources || []).some((association) => (association.inventory?.length ?? 0) > 0);

/** Sanitizes inventory associations for a single resource booking plan */
export const sanitizeResourcePlanInventory = (
  resource: ResourceSchema,
  associatedResources?: PlanResourceAssociation[],
  inventoryItemsById?: Map<string, InventorySchema>,
  planMaxHours = 0
): PlanResourceAssociation[] | undefined => {
  const resourceId = resource.id || "";
  if (!resourceId) {
    return undefined;
  }

  const existingAssociation = (associatedResources || []).find(
    (association) => association.resource_id === resourceId
  );
  const inventory = (existingAssociation?.inventory || [])
    .filter((selection) => selection.inventory_id && selection.max_qty > 0)
    .map((selection) => {
      const item = inventoryItemsById?.get(selection.inventory_id);
      if (!item) {
        return selection;
      }
      return {
        ...selection,
        max_qty: clampInventoryMaxQty(selection.max_qty, item),
      };
    });

  if (inventory.length === 0) {
    return undefined;
  }

  return [{
    resource_id: resourceId,
    resource_name: resource.name || existingAssociation?.resource_name,
    max_duration: planMaxHours,
    inventory,
  }];
};

/** Resolves resource plan type: dual only when bundled inventory is present */
export const resolveResourcePlanType = (
  associatedResources?: PlanResourceAssociation[]
): PlanType =>
  resourcePlanHasInventory(associatedResources) ? "dual" : "resource";

/** Whether a resource plan has at least one linked resource association */
export const resourcePlanHasValidAssociatedResources = (
  associatedResources?: PlanResourceAssociation[]
): boolean =>
  (associatedResources || []).some((association) => Boolean(association.resource_id));

/** Sanitizes resource-plan associations, preserving the resource link even without inventory */
export const sanitizeResourcePlanAssociations = (
  resource: ResourceSchema,
  associatedResources?: PlanResourceAssociation[],
  inventoryItemsById?: Map<string, InventorySchema>,
  planMaxHours = 0
): PlanResourceAssociation[] => {
  const withInventory = sanitizeResourcePlanInventory(
    resource,
    associatedResources,
    inventoryItemsById,
    planMaxHours
  );
  if (withInventory) {
    return withInventory;
  }

  const resourceId = resource.id || "";
  if (!resourceId) {
    return [];
  }

  const existingAssociation = (associatedResources || []).find(
    (association) => association.resource_id === resourceId
  );
  if (!existingAssociation?.resource_id) {
    return [];
  }

  return [{
    resource_id: resourceId,
    resource_name: resource.name || existingAssociation.resource_name,
    max_duration: planMaxHours,
    inventory: [],
  }];
};

/** Whether associated_resources should be enabled on a resource booking plan form */
export const resourcePlanIncludesAssociatedResources = (
  plan?: Pick<PlanSchema, "plan_type" | "associated_resources">
): boolean =>
  resourcePlanHasInventory(plan?.associated_resources) ||
  (
    plan?.plan_type === "dual" &&
    resourcePlanHasValidAssociatedResources(plan?.associated_resources)
  );

export const sanitizeAssociatedResources = (
  associatedResources?: PlanResourceAssociation[],
  inventoryItemsById?: Map<string, InventorySchema>,
  planMaxHours?: number
): PlanResourceAssociation[] | undefined => {
  const filtered = limitAssociatedResources(
    (associatedResources || [])
      .map(normalizePlanResourceAssociation)
      .filter((association) => association.resource_id)
      .map((association) => ({
        resource_id: association.resource_id,
        resource_name: association.resource_name,
        max_duration: planMaxHours !== undefined ? planMaxHours : association.max_duration,
        inventory: (association.inventory || [])
          .filter((selection) => selection.inventory_id && selection.max_qty > 0)
          .map((selection) => {
            const item = inventoryItemsById?.get(selection.inventory_id);
            if (!item) return selection;
            return {
              ...selection,
              max_qty: clampInventoryMaxQty(selection.max_qty, item),
            };
          }),
      }))
  );
  return filtered.length > 0 ? filtered : undefined;
};

export const resolveMembershipPlanType = (
  associatedResources?: PlanResourceAssociation[]
): "membership" | "dual" => {
  const hasAssociations = (associatedResources || []).some(
    (association) => association.resource_id
  );
  return hasAssociations ? "dual" : "membership";
};

/**
 * Merges a resource's booking plans with the dual membership tiers linked to it.
 * Dual plans are re-sourced from the shared community plans list (the sync source of
 * truth) so membership-tier edits stay reflected in the resource booking plans view.
 */
export const mergeResourcePlansWithDualTiers = (
  resourcePlans: ResourcePlanSchema[] | undefined,
  communityPlans: PlanSchema[] | undefined,
  resourceId: string
): ResourcePlanSchema[] => {
  const byId = new Map<string, ResourcePlanSchema>();

  for (const plan of resourcePlans || []) {
    if (plan.plan_type === "dual") continue;
    const key = plan.plan_id || `resource-plan-${byId.size}`;
    byId.set(key, plan);
  }

  for (const plan of communityPlans || []) {
    const isDualForResource =
      plan.plan_type === "dual" &&
      (plan.associated_resources || []).some(
        (association) => association.resource_id === resourceId
      );
    if (!isDualForResource || !plan.plan_id) continue;
    byId.set(plan.plan_id, plan as ResourcePlanSchema);
  }

  return Array.from(byId.values());
};
